import { useNavigate } from "@/lib/router-compat";
import { SiteLinksDirectory } from "@/components/SiteLinksDirectory";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Users,
  Calendar,
  Shuffle,
  Bell,
  CheckCircle,
  ArrowRight,
  Building2,
  Zap,
  Shield,
  Menu,
  Clock,
  Smartphone,
  XCircle,
  MapPin,
  ChevronDown,
  CreditCard,
  UserPlus,
  Timer,
  Footprints,
  ListChecks,
  Radio,
  CalendarCheck,
  CircleDot,
} from "lucide-react";
import { PRICING_TIERS, formatPrice, getExampleCostsPerMember, type PricingTier, type BillingInterval } from "@/lib/pricing";
import husselLogo from "@/assets/hussel-app-logo-wide.svg";
import { ClubCarousel } from "@/components/ClubCarousel";
const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [husselOpen, setHusselOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('yearly');

  // Auto-redirect logged-in users from the homepage to their dashboard.
  // They can still visit other content pages by navigating directly.
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();
        const target = (roleData?.role === 'super-admin' || roleData?.role === 'husselaar') ? '/admin' : '/dashboard';
        navigate(target, { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const features = [
    {
      icon: Shuffle,
      title: "Slimme Automatische Indeling",
      description:
        "Het algoritme houdt rekening met speelsterkte, aantal gespeelde rondes, eerdere tegenstanders, rustperiodes en beschikbaarheid. Zo ontstaat elke ronde een eerlijke en gevarieerde indeling.",
    },
    {
      icon: Smartphone,
      title: "Aanmelden via Mobiel",
      description: "Leden melden zich eenvoudig aan via hun telefoon. Geen pasjes, briefjes of lijsten meer nodig.",
    },
    {
      icon: Clock,
      title: "Realtime Overzicht",
      description:
        "Spelers zien direct op hun telefoon met wie ze spelen en op welke baan. Geen terugloop naar het clubhuis.",
    },
    {
      icon: Bell,
      title: "Push Notificaties",
      description:
        "Automatische meldingen bij nieuwe rondes, wijzigingen of afgelasting. Iedereen weet direct waar ze moeten zijn.",
    },
    {
      icon: Users,
      title: "Geen Vrijwilligers Nodig",
      description: "Geen husselcommissie of husselaar meer nodig die dag. Het systeem draait volledig zelfstandig.",
    },
    {
      icon: Users,
      title: "Zie vooraf wie er komt",
      description:
        "Laat met één klik weten dat je komt en zie welke clubgenoten er ook zijn. Zo trek je elkaar over de streep.",
    },
    {
      icon: Shield,
      title: "Club Branding",
      description:
        "De app toont het logo en de naam van jouw club. Volledig white-label voor een professionele uitstraling.",
    },
  ];
  const benefits = [
    {
      title: "Minder regelen",
      items: [
        { icon: Users, text: "Geen vrijwilligers nodig tijdens het spelen" },
        { icon: Shuffle, text: "Geen husselcommissie of tossleider nodig" },
        { icon: Smartphone, text: "Aanmelden via je telefoon" },
      ],
    },
    {
      title: "Meer tijd op de baan",
      items: [
        { icon: Footprints, text: "Niet teruglopen naar het clubhuis" },
        { icon: Timer, text: "Meer speeltijd voor iedereen" },
        { icon: MapPin, text: "Je indeling direct op je telefoon" },
      ],
    },
    {
      title: "Samen komen spelen",
      items: [
        { icon: UserPlus, text: "Laat vooraf weten dat je komt en zie wie ook komt" },
        { icon: ListChecks, text: "Zie wie er is aangemeld" },
        { icon: CircleDot, text: "Voor tennis én padel" },
      ],
    },
    {
      title: "Altijd op de hoogte",
      items: [
        { icon: Bell, text: "Een melding bij elke nieuwe ronde" },
        { icon: CalendarCheck, text: "Weet of de sessie doorgaat" },
        { icon: Radio, text: "Ook op afstand te gebruiken" },
      ],
    },
  ];
  const manualTasks = [
    {
      title: "Banen reserveren",
      description: "Iemand moet natuurlijk wel banen vrijhouden voor de sessie",
    },
    {
      title: "Sessie aanmaken",
      description: "Een beheerder maakt de hussel-/tossessie aan in het systeem",
    },
    {
      title: "Belsignaal (optioneel)",
      description: "Een belletje of alarm als de ronde voorbij is - tip: alarm op telefoon",
    },
  ];
  const steps = [
    {
      number: "1",
      title: "Registreer je Club",
      description: "Maak een account aan en registreer je tennis- of padelclub in een paar minuten.",
    },
    {
      number: "2",
      title: "Configureer Banen",
      description: "Voeg je banen toe met type, verlichting en indeling die past bij jouw park.",
    },
    {
      number: "3",
      title: "Nodig Leden Uit",
      description: "Deel de link met je leden. Zij maken eenmalig een account aan met hun niveau.",
    },
    {
      number: "4",
      title: "Start met Husselen",
      description: "De app regelt de rest - automatische indelingen, notificaties en meer.",
    },
  ];
  const menuItemsBeforeHusselen: { label: string; href: string; isAnchor?: boolean }[] = [
    {
      label: "Hoe werkt het?",
      href: "/hoe-werkt-het",
    },
    {
      label: "Over",
      href: "/over-de-hussel-app",
    },
  ];

  const menuItemsAfterHusselen: { label: string; href: string; isAnchor?: boolean }[] = [
    {
      label: "Reviews",
      href: "/reviews",
    },
    {
      label: "Prijzen",
      href: "#pricing",
      isAnchor: true,
    },
    {
      label: "Demo",
      href: "/demo",
    },
    {
      label: "Contact",
      href: "/contact",
    },
  ];

  const husselMenuItems = [
    { label: "Wat is husselen?", href: "/wat-is-husselen" },
    { label: "Wat is tossen?", href: "/wat-is-tossen" },
    { label: "Husselen vs tossen vs racket trekken", href: "/husselen-vs-tossen-vs-racket-trekken" },
    { label: "Husselavond organiseren", href: "/husselavond-organiseren" },
    { label: "Tossavond organiseren", href: "/tossavond-organiseren" },
    { label: "Racket trekken organiseren", href: "/racket-trekken-organiseren" },
    { label: "Tennisclub software", href: "/tennisclub-software" },
    { label: "Nieuws", href: "/nieuws" },
  ];

  const handleNavigation = (item: { href: string; isAnchor?: boolean }) => {
    if (item.isAnchor) {
      document.getElementById(item.href.replace("#", ""))?.scrollIntoView({
        behavior: "smooth",
      });
    } else {
      navigate(item.href);
    }
    setMobileMenuOpen(false);
  };
  return (
    <div className="min-h-screen bg-background">
      {/* Beta launch banner */}
      <div className="relative z-40 w-full text-center text-xs sm:text-sm px-3 py-2 bg-primary text-primary-foreground">
        <span className="font-semibold">🚀 Beta — gratis tot en met 31 oktober 2026.</span>{" "}
        <span className="hidden sm:inline">Help ons doorontwikkelen met je feedback.</span>{" "}
        <a href="/club-register" className="ml-2 underline font-semibold">Registreer je club gratis →</a>
      </div>
      {/* Hero Section */}
        <nav className="sticky top-0 z-50 w-full px-4 sm:px-6 lg:px-8 py-3 bg-background border-b border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/" aria-label="De Hussel App home"><img src={husselLogo} alt="De Hussel App" className="h-10 md:h-12 w-auto object-contain" /></a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              {menuItemsBeforeHusselen.map((item) => (
                <a
                  key={item.label}
                  href={item.isAnchor ? "/#pricing" : item.href}
                  onClick={(e) => { if (item.isAnchor) { e.preventDefault(); handleNavigation(item); } }}
                  className="text-foreground/70 hover:text-primary transition-colors px-3 py-2"
                >
                  {item.label}
                </a>
              ))}
              
              {/* Husselen dropdown with hover */}
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-foreground/70 hover:text-primary hover:bg-muted data-[state=open]:bg-muted data-[state=open]:text-primary h-10 px-3 text-base font-normal">
                      Husselen
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[280px] gap-1 p-2 bg-popover">
                        {husselMenuItems.map((item) => (
                          <li key={item.label}>
                            <NavigationMenuLink asChild>
                              <a
                                href={item.href}
                                className="block w-full text-left select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer"
                              >
                                {item.label}
                              </a>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {menuItemsAfterHusselen.map((item) => (
                <a
                  key={item.label}
                  href={item.isAnchor ? "/#pricing" : item.href}
                  onClick={(e) => { if (item.isAnchor) { e.preventDefault(); handleNavigation(item); } }}
                  className="text-foreground/70 hover:text-primary transition-colors px-3 py-2"
                >
                  {item.label}
                </a>
              ))}

              <div className="flex items-center gap-3 ml-4">
                <Button asChild
                  variant="outline"
                  className="border-border text-foreground hover:bg-muted gap-1.5"
                >
                  <a href="/auth">Inloggen <span className="text-muted-foreground">/</span> <UserPlus className="h-4 w-4" /></a>
                </Button>
                <Button asChild
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a href="/club-register">Club registreren</a>
                </Button>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center gap-2">
              <Button asChild
                variant="outline"
                size="sm"
                className="border-border text-foreground hover:bg-muted gap-1.5"
              >
                <a href="/auth">Inloggen <span className="text-muted-foreground">/</span> <UserPlus className="h-4 w-4" /></a>
              </Button>
              <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-border text-foreground hover:bg-muted"
                    aria-label="Menu openen"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
                  {menuItemsBeforeHusselen.map((item) => (
                    <DropdownMenuItem key={item.label} asChild>
                      <a href={item.isAnchor ? "/#pricing" : item.href} onClick={(e) => { if (item.isAnchor) { e.preventDefault(); handleNavigation(item); } }}>{item.label}</a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setHusselOpen((o) => !o); }} className="justify-between">
                  Husselen <ChevronDown className={`h-4 w-4 transition-transform ${husselOpen ? "rotate-180" : ""}`} />
                </DropdownMenuItem>
                {husselOpen && (<div className="pl-3 border-l border-border ml-2">
                      {husselMenuItems.map((item) => (
                        <DropdownMenuItem key={item.label} asChild>
                          <a href={item.href}>{item.label}</a>
                        </DropdownMenuItem>
                      ))}
                    </div>)}
                  {menuItemsAfterHusselen.map((item) => (
                    <DropdownMenuItem key={item.label} asChild>
                      <a href={item.isAnchor ? "/#pricing" : item.href} onClick={(e) => { if (item.isAnchor) { e.preventDefault(); handleNavigation(item); } }}>{item.label}</a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem asChild>
                    <a href="/club-register">Club registreren</a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </nav>
      <header className="relative border-b border-border">

        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" />
        </div>



        <div className="relative z-10 container mx-auto max-w-6xl px-3 py-10 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="max-w-3xl">
              <Badge className="mb-4 bg-accent/20 text-accent border-accent/30">Voor Tennis- & Padelclubs</Badge>
              <h1 className="text-4xl md:text-7xl text-primary mb-3 leading-tight">De Hussel App</h1>
              <h2 className="text-xl md:text-3xl text-accent mb-6 leading-snug">
                Automatiseer je Hussel- en Tossavond
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                Tennis- en padelclubs organiseren vaak wekelijks hussel-avonden, toss-momenten of racket-trekken. Dit
                wordt vaak georganiseerd door een vrijwilligerscommissie van de club. Dit legt druk op leden van de club
                en kost onnodig tijd. De Hussel App maakt slimme indelingen op basis van speelsterkte, gespeelde rondes en eerdere tegenstanders — volledig automatisch, geen commissie meer nodig!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild
                  size="lg"
                  className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
                >
                  <a href="/club-register">
                  Registreer gratis t/m okt 2026
                  <ArrowRight className="h-5 w-5" />
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border text-foreground hover:bg-muted"
                  onClick={() =>
                    document.getElementById("pricing")?.scrollIntoView({
                      behavior: "smooth",
                    })
                  }
                >
                  Bekijk prijzen
                </Button>
              </div>
            </div>

            {/* Phone mockup – fictieve baanindeling */}
            <div className="hidden lg:flex justify-center lg:justify-end">
              <div
                className="relative"
                style={{
                  width: 280,
                  height: 570,
                  borderRadius: "2.75rem",
                  background: "#171717",
                  border: "10px solid #262626",
                  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.7)",
                  overflow: "hidden",
                }}
              >
                <div
                  className="absolute top-2 left-1/2 -translate-x-1/2 z-20"
                  style={{ width: 112, height: 24, background: "#171717", borderRadius: 9999 }}
                />
                <div className="absolute inset-0 flex flex-col bg-background text-foreground">
                  <div className="h-10 flex items-center justify-between px-6 pt-2 text-[10px] text-foreground/70">
                    <span>20:14</span>
                    <span>•••</span>
                  </div>
                  <div className="px-4 pt-2 pb-3 border-b border-border">
                    <div className="uppercase tracking-wide text-muted-foreground text-[10px]">Huidige ronde</div>
                    <div className="flex items-baseline justify-between mt-0.5">
                      <div className="text-base font-semibold">Ronde 3</div>
                      <div className="text-accent font-medium text-[10px]">20:00 – 20:30</div>
                    </div>
                  </div>
                  <div className="flex-1 px-3 py-3 space-y-2 overflow-hidden">
                    {[
                      { court: "Baan 1", a: ["Lotte", "Sem"], b: ["Daan", "Iris"] },
                      { court: "Baan 2", a: ["Noor", "Tim"], b: ["Eva", "Bram"] },
                      { court: "Baan 3", a: ["Anne", "Joep"], b: ["Mila", "Ruben"] },
                      { court: "Baan 4", a: ["Sanne", "Luuk"], b: ["Fien", "Max"] },
                    ].map((c) => (
                      <div key={c.court} className="rounded-lg border border-border bg-card/60 p-2.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="font-semibold text-[11px]">{c.court}</div>
                          <div className="text-accent text-[9px]">Dubbel</div>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <div className="flex-1">
                            <div>{c.a[0]}</div>
                            <div>{c.a[1]}</div>
                          </div>
                          <div className="px-2 text-muted-foreground text-[9px]">vs</div>
                          <div className="flex-1 text-right">
                            <div>{c.b[0]}</div>
                            <div>{c.b[1]}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2.5 border-t border-border flex items-center justify-between text-muted-foreground text-[10px]">
                    <span>Volgende ronde</span>
                    <span className="text-foreground font-medium">over 14 min</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </header>

      <main>
        {/* Problem Statement Section */}
        <section className="py-10 md:py-24 px-3 bg-background/90 backdrop-blur-sm">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Hoe het nu vaak gaat</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Veel clubs organiseren wekelijks een vast moment waarop leden kunnen komen "husselen", "tossen" of
                "racket-trekken". Het principe is overal hetzelfde:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8 md:mb-12">
              <Card className="p-4 md:p-6 bg-card border-border">
                <h3 className="text-xl font-semibold text-foreground mb-4">Het traditionele proces</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>De club organiseert een vast moment (vaak structureel dezelfde dag/tijd)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Er worden banen vrijgehouden voor een paar uur</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Leden melden zich aan door hun pasje in te leveren of naam op te schrijven</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shuffle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>De commissie "husselt" de leden door elkaar op basis van clubregels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Dit wisselt per 30-45 minuten (dubbelspel, mix, op niveau)</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-4 md:p-6 bg-destructive/10 border-destructive/30">
                <h3 className="text-xl font-semibold text-foreground mb-4">De uitdagingen</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span>Clubs zijn afhankelijk van vrijwilligers die dit willen doen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span>Dit kost structureel veel tijd van dezelfde mensen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span>Leden moeten teruglopen naar het clubhuis voor de nieuwe indeling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span>Dat kost speeltijd en zorgt voor gedoe</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span>Commissieleden kunnen zelf niet altijd meespelen</span>
                  </li>
                </ul>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-lg md:text-xl text-accent font-semibold">
                De Hussel App — slimme, volledig geautomatiseerde hussel- en tossavonden
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section aria-labelledby="benefits-heading" className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 id="benefits-heading" className="text-2xl md:text-4xl font-bold text-foreground mb-4">De voordelen van De Hussel App</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Alles wat een husselcommissie doet, volledig geautomatiseerd.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-5 gap-y-7 md:gap-x-10 md:gap-y-9 lg:grid-cols-4">
              {benefits.map((group) => (
                <div key={group.title} className="min-w-0 border-t-2 border-primary/60 pt-3 md:pt-4">
                  <h3 className="text-base leading-snug text-accent mb-4 md:text-xl">{group.title}</h3>
                  <ul className="space-y-4">
                    {group.items.map(({ icon: Icon, text }) => (
                      <li key={text} className="flex items-start gap-2 md:gap-3">
                        <Icon aria-hidden="true" strokeWidth={1.75} className="mt-0.5 h-4 w-4 shrink-0 text-accent md:h-5 md:w-5" />
                        <span className="text-xs leading-relaxed text-foreground md:text-sm">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClubCarousel />

        {/* Features Section */}
        <section className="py-10 md:py-24 px-3 bg-background/90 backdrop-blur-sm">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Hoe werkt het?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Van aanmelden tot indeling - De Hussel App regelt het allemaal automatisch.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-4 md:p-6 bg-card border-border hover:border-accent/50 transition-colors">
                  <div className="bg-accent/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Still Needs Manual Work */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Wat moet de club nog wel doen?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Een paar dingen blijven nog nodig - de rest verloopt vanzelf.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {manualTasks.map((task, index) => (
                <div key={index} className="text-center">
                  <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{task.title}</h3>
                  <p className="text-muted-foreground text-sm">{task.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="py-10 md:py-24 px-3 bg-background/90 backdrop-blur-sm">
          <div className="container mx-auto max-w-6xl">
            <Card className="p-5 md:p-12 bg-card border-border">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Aan de slag in 4 stappen</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  In vier eenvoudige stappen is je club klaar om te husselen.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-muted-foreground text-sm">{step.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Prijzen op maat voor jouw club</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Kies het pakket dat past bij de grootte van jouw club. Alle pakketten bevatten dezelfde functies.
              </p>
              <div className="mt-6 inline-block rounded-full px-4 py-2 font-semibold text-sm" style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}>
                🎉 Tijdens de beta: gratis t/m 31 oktober 2026 — geen betaalmethode nodig
              </div>
              <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto">
                Onderstaande prijzen gelden vanaf 1 november 2026. Je kunt nu alvast registreren en het hele platform gratis uitproberen.
              </p>
            </div>


            {/* Billing Interval Toggle */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Maandelijks
              </span>
              <Switch
                checked={billingInterval === 'yearly'}
                onCheckedChange={(checked) => setBillingInterval(checked ? 'yearly' : 'monthly')}
              />
              <span className={`text-sm font-medium ${billingInterval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Jaarlijks
              </span>
              {billingInterval === 'yearly' && (
                <Badge className="bg-primary/10 text-primary border-primary/30 animate-in fade-in">
                  Bespaar tot 44%
                </Badge>
              )}
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8 md:mb-12">
              {PRICING_TIERS.map((tier) => {
                const displayPrice = billingInterval === 'yearly' ? tier.priceExclBTW : tier.monthlyPriceExclBTW;
                const displayPriceInclBTW = billingInterval === 'yearly' ? tier.priceInclBTW : tier.monthlyPriceInclBTW;
                return (
                <Card
                  key={tier.id}
                  className={`p-6 relative overflow-hidden transition-all ${tier.isPopular ? "border-2 border-primary bg-primary/5 scale-105 shadow-xl" : "bg-card border-border hover:border-primary/50"}`}
                >
                  {tier.isPopular && (
                    <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">Meest gekozen</Badge>
                  )}
                  {billingInterval === 'yearly' && (
                    <Badge className={`absolute ${tier.isPopular ? 'top-12' : 'top-4'} right-4 bg-primary/10 text-primary border-primary/30 text-xs`}>
                      -{tier.savingsPercent}%
                    </Badge>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-foreground mb-1">{tier.name}</h3>
                    <p className="text-muted-foreground text-sm">{tier.memberRange}</p>
                  </div>

                  <div className="mb-6">
                    {billingInterval === 'yearly' && (
                      <p className="text-sm text-muted-foreground line-through mb-1">
                        €{formatPrice(tier.monthlyPriceExclBTW)}/maand
                      </p>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg text-muted-foreground">€</span>
                      <span className={`text-4xl font-bold ${tier.isPopular ? "text-accent" : "text-foreground"}`}>
                        {formatPrice(displayPrice)}
                      </span>
                      <span className="text-muted-foreground">/maand</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      excl. 21% BTW (€{formatPrice(displayPriceInclBTW)} incl.)
                    </p>
                    {billingInterval === 'yearly' && (
                      <p className="text-xs text-primary mt-1 font-medium">
                        €{formatPrice(tier.yearlyTotalExclBTW)}/jaar · Bespaar €{formatPrice(tier.savingsTotal)}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6">
                    {[
                      "Onbeperkt aantal sessies",
                      "Automatische indelingen",
                      "Push notificaties",
                      "Club branding",
                      "Live dashboard",
                      "Banen beheer",
                      "Meldingen beheer",
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button asChild
                    size="lg"
                    className={`w-full gap-2 ${tier.isPopular ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}`}
                    variant={tier.isPopular ? "default" : "outline"}
                  >
                    <a href="/club-register">
                    <Building2 className="h-5 w-5" />
                    Kies {tier.name.toLowerCase()}
                    </a>
                  </Button>
                </Card>
                );
              })}
            </div>

            {/* Cost per member examples */}
            <Card className="p-6 md:p-8 bg-card border-border">
              <h3 className="text-xl font-bold text-foreground mb-4 text-center">Kosten per lid per maand</h3>
              <p className="text-muted-foreground text-center mb-6">
                Zie hoe voordelig De Hussel App is, ongeacht de grootte van je club
              </p>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pakket</TableHead>
                      <TableHead className="text-center">Scenario 1</TableHead>
                      <TableHead className="text-center">Scenario 2</TableHead>
                      <TableHead className="text-center">Scenario 3</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {PRICING_TIERS.map((tier) => {
                      const examples = getExampleCostsPerMember(tier.id as PricingTier, billingInterval);
                      const displayPrice = billingInterval === 'yearly' ? tier.priceExclBTW : tier.monthlyPriceExclBTW;
                      return (
                        <TableRow key={tier.id} className={tier.isPopular ? "bg-accent/10" : ""}>
                          <TableCell className="font-medium">
                            <div>
                              <p className="font-semibold">{tier.name}</p>
                              <p className="text-xs text-muted-foreground">€{formatPrice(displayPrice)}/mnd</p>
                            </div>
                          </TableCell>
                          {examples.map((example, i) => (
                            <TableCell key={i} className="text-center">
                              <p className="font-semibold text-accent">€{example.costPerMember}</p>
                              <p className="text-xs text-muted-foreground">bij {example.members} leden</p>
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="text-center mt-6 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {billingInterval === 'yearly' 
                    ? 'Jaarabonnement: 12 maanden, maandelijks opzegbaar na het eerste jaar. Alle prijzen excl. 21% BTW.'
                    : 'Maand-Flex: maandelijks opzegbaar. Alle prijzen excl. 21% BTW.'}
                </p>
                <p className="text-xs text-muted-foreground">
                  * Het aantal leden betreft het totaal aantal <strong className="text-foreground">actieve clubleden</strong> ingeschreven bij de club (conform KNLTB-registratie), niet het aantal app-gebruikers. De clubgrootte wordt door ons geverifieerd en indien nodig wordt het pakket aangepast.
                </p>
                <div className="flex items-center justify-center gap-3 pt-2">
                  <span className="text-xs text-muted-foreground">Betaalmethoden:</span>
                  <div className="flex items-center gap-2">
                    <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/directdebit.svg" alt="SEPA Incasso" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Creditcard" className="h-6" width={32} height={24} />
                  </div>
                </div>
              </div>
            </Card>

            {/* Beta disclaimer */}
            <div className="mt-8 max-w-3xl mx-auto p-5 rounded-lg border-2 border-accent/40 bg-accent/5 text-center">
              <p className="font-semibold text-foreground">🚀 De Hussel App is op dit moment een beta-versie</p>
              <p className="text-sm text-muted-foreground mt-1">
                Er kunnen nog kleine fouten in zitten. Tijdens deze beta-periode is het platform gratis t/m 31 oktober 2026 — jouw feedback helpt ons direct met doorontwikkelen.
              </p>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-4xl">
            <Card className="p-5 md:p-12 bg-accent/10 border-accent/30 text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Klaar om te starten?</h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Sluit je aan bij de tennis- en padelclubs die hun husselsessies al efficiënter organiseren met De Hussel
                App.
              </p>
              <Button asChild
                size="lg"
                className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <a href="/club-register">
                Registreer je Club
                <ArrowRight className="h-5 w-5" />
                </a>
              </Button>
            </Card>
          </div>
        </section>
      </main>


      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <SiteLinksDirectory />
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src={husselLogo} alt="De Hussel App" className="h-10 w-auto object-contain" />
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} De Hussel App. Alle rechten voorbehouden.
              </p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <a href="/algemene-voorwaarden" className="hover:text-primary transition-colors">Algemene Voorwaarden</a>
                <span>•</span>
                <a href="/privacy-website" className="hover:text-primary transition-colors">Privacybeleid</a>
                <span>•</span>
                <a href="/contact" className="hover:text-primary transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
export default Landing;
